import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, IconButton, Stack, Typography, Box, CircularProgress, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { translate as t } from 'hooks/useTranslations';
import { Policy, PolicyPrefaceHeader } from 'views/App/Portal/Policy/_types/policy';
import { ManualPreface, Attachment } from 'views/App/Portal/Policy/_types/manuals/draft';
import PropertyList, { PropertyListItem } from 'components/_layouts/PropertyList';
import useManageAttachment from '../../DraftManuals/_data_sources/useManageAttachment';
import brokenImage from 'components/ContentEditor/images/image-broken.svg';

type PolicyHeadersProps = {
  policy?: Policy;
  preface?: ManualPreface;
  headers?: PolicyPrefaceHeader[];
  translationPrefix?: 'stagedManuals' | 'issuedManuals';
  manualId?: string;
};

const policyHeaderKeys = ['effectiveDate', 'revisedDate', 'issuingAuthority'];

const IMAGE_CONTAINER_SX = {
  height: '60px',
  minWidth: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const IMAGE_STYLE = {
  maxHeight: '60px',
  maxWidth: '200px',
  objectFit: 'contain' as const,
};

const getAttachmentKey = (type: 'agencyLogo' | 'signature', id: string): string => {
  return `${type}_${id}`;
};

const PolicyHeaders: React.FC<PolicyHeadersProps> = ({
  policy,
  preface,
  headers: directHeaders,
  translationPrefix = 'stagedManuals',
  manualId,
}: PolicyHeadersProps) => {
  const [collapsed, setCollapsed] = React.useState<boolean>(true);
  const { getAttachment: fetchAttachment } = useManageAttachment();
  const [attachments, setAttachments] = useState<Record<string, Attachment>>({});
  const [loadingAttachments, setLoadingAttachments] = useState<Record<string, boolean>>({});
  const [errorAttachments, setErrorAttachments] = useState<Record<string, boolean>>({});
  const fetchedAttachmentsRef = useRef<Set<string>>(new Set());
  const headers = directHeaders || policy?.headers || preface?.headers;

  const getHeaderLabel = (key: string): string => {
    return t(`policy.${translationPrefix}.policyHeaders.${key}`);
  };

  const renderAttachmentImage = (
    attachmentKey: string,
    imageUrl: string,
    altText: string
  ): React.ReactNode => {
    const isLoading = loadingAttachments[attachmentKey];
    const hasError = errorAttachments[attachmentKey];

    return (
      <Box sx={IMAGE_CONTAINER_SX}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : hasError ? (
          <img
            src={brokenImage}
            alt="Failed to load"
            style={{
              height: '60px',
              width: '100%',
              opacity: 0.2,
            }}
          />
        ) : (
          <img
            src={imageUrl}
            alt={altText}
            style={IMAGE_STYLE}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              setErrorAttachments((prev) => ({ ...prev, [attachmentKey]: true }));
            }}
          />
        )}
      </Box>
    );
  };

  const renderDateHeader = (type: 'effectiveDate' | 'revisedDate', header: PolicyPrefaceHeader) => {
    if (header.value) {
      headerItems.push({
        label: getHeaderLabel(type),
        // value: formatDate(header.value, header.dateFormat),
        value: header.value,
      });
    }
  };

  // Fetch attachments when headers change
  useEffect(() => {
    if (!headers || !manualId) return;

    const attachmentIdsToFetch: Array<{ id: string; key: string; }> = [];

    // Helper to queue attachment for fetching if not already attempted
    const queueAttachment = (attachmentId: string | undefined, keyPrefix: string) => {
      if (!attachmentId) return;

      const key = `${keyPrefix}_${attachmentId}`;
      if (!fetchedAttachmentsRef.current.has(key)) {
        attachmentIdsToFetch.push({ id: attachmentId, key });
        fetchedAttachmentsRef.current.add(key);
      }
    };

    headers.forEach((header) => {
      if (header.state === 'DISABLED') return;

      if (header.type === 'AGENCY_LOGO' && header.agencyLogo) {
        queueAttachment(header.agencyLogo, 'agencyLogo');
      } else if (header.type === 'ISSUING_AUTHORITY' && header.issuingAuthoritySignature) {
        queueAttachment(header.issuingAuthoritySignature, 'signature');
      }
    });

    // Fetch all attachments that need to be fetched
    attachmentIdsToFetch.forEach(({ id, key }) => {
      setLoadingAttachments((prev) => ({ ...prev, [key]: true }));

      fetchAttachment({ manualId, attachmentId: id })
        .then((response) => {
          setAttachments((prev) => ({ ...prev, [key]: response as any }));
          setLoadingAttachments((prev) => ({ ...prev, [key]: false }));
        })
        .catch((error) => {
          console.error(`Failed to fetch attachment ${id}:`, error);
          setErrorAttachments((prev) => ({ ...prev, [key]: true }));
          setLoadingAttachments((prev) => ({ ...prev, [key]: false }));
        });
    });
  }, [headers, manualId]);

  let headerItems: PropertyListItem[] = [];

  if (headers && headers.length > 0) {
    headers.forEach((header) => {
      if (header.state === 'DISABLED') {
        return;
      }

      switch (header.type) {
        case 'EFFECTIVE_DATE':
          renderDateHeader('effectiveDate', header);
          break;

        case 'REVISED_DATE':
          renderDateHeader('revisedDate', header);
          break;

        case 'AGENCY_LOGO':
          if (header.agencyLogo) {
            const attachmentKey = getAttachmentKey('agencyLogo', header.agencyLogo);
            const attachment = attachments[attachmentKey];
            const imageUrl = attachment?.url || header.agencyLogo;

            headerItems.push({
              label: getHeaderLabel('agencyLogo') || 'Agency Logo',
              value: renderAttachmentImage(attachmentKey, imageUrl, 'Agency Logo'),
            });
          }
          break;

        case 'ISSUING_AUTHORITY':
          const issuingAuthType = header.issuingAuthorityType ?? 'SIGNATURE_AND_NAME';
          let issuingAuthValue: React.ReactNode = null;

          if (issuingAuthType === 'SIGNATURE_AND_NAME' || issuingAuthType === 'SIGNATURE_ONLY') {
            if (header.issuingAuthoritySignature) {
              const signatureKey = getAttachmentKey('signature', header.issuingAuthoritySignature);
              const signatureAttachment = attachments[signatureKey];
              const signatureUrl = signatureAttachment?.url || header.issuingAuthoritySignature;

              issuingAuthValue = (
                <Stack direction="column" spacing={1} alignItems="flex-start">
                  {renderAttachmentImage(signatureKey, signatureUrl, 'Signature')}
                  {issuingAuthType === 'SIGNATURE_AND_NAME' && header.issuingAuthorityName && (
                    <Typography>{header.issuingAuthorityName}</Typography>
                  )}
                </Stack>
              );
            } else if (issuingAuthType === 'SIGNATURE_AND_NAME' && header.issuingAuthorityName) {
              issuingAuthValue = header.issuingAuthorityName;
            }
          } else if (issuingAuthType === 'NAME_ONLY' && header.issuingAuthorityName) {
            issuingAuthValue = header.issuingAuthorityName;
          }

          if (issuingAuthValue) {
            headerItems.push({
              label: getHeaderLabel('issuingAuthority'),
              value: issuingAuthValue,
            });
          }
          break;

        case 'CUSTOM1':
        case 'CUSTOM2':
          if (header.header && header.value) {
            headerItems.push({
              label: header.header,
              value: header.value,
            });
          }
          break;
      }
    });
  } else if (policy) {
    Object.entries(policy).forEach(([key, value]) => {
      if (policyHeaderKeys.includes(key) && value) {
        headerItems.push({
          label: getHeaderLabel(key),
          value: value?.toString(),
        });
      }
    });
  }

  return headerItems.length > 0 ? (
    <Card>
      <Box
        onClick={() => setCollapsed((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          py: 3,
          cursor: 'pointer',
          borderBottom: !collapsed ? '1px solid' : 'none',
          borderBottomColor: 'divider',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Typography variant="h6"> {t(`policy.${translationPrefix}.policyHeaders.title`)}</Typography>
        <IconButton size="small">
          {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>
      <Collapse in={!collapsed}>
        <CardContent sx={{ py: 6, px: { xs: 4, sm: 10 } }}>
          <PropertyList
            columns={3}
            items={headerItems}
          />
        </CardContent>
      </Collapse>
    </Card>
  ) : null;
};

export default PolicyHeaders;
